// Smooth area/line trend shared by the Reports B trend cards. Each series is an
// <Area>: give it gradient `stops` ([offset %, colour, opacity]) for a filled
// band, or leave them off (optionally `dashed`) for a plain line. xAxis, yAxis
// and tooltip are spread onto the matching Recharts element.
import React, { useId } from 'react';
import { useTheme } from '@mui/material/styles';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const RED_FADE = [[0, '#FF6B7A', 0.22], [50, '#FF5252', 0.08], [100, '#FF5252', 0]];
export const GREEN_FADE = [[0, '#4ADE80', 0.18], [50, '#66BB6A', 0.07], [100, '#66BB6A', 0]];

const NO_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };
const MARGIN = { top: 8, right: 4, bottom: 0, left: 4 };

export default function TrendAreaChart({ data, xKey, series, xAxis, yAxis, tooltip, sparkline = false, animate = false }) {
    const theme = useTheme();
    // useId gives ":r1:", and the colons break url(#...) fill references.
    const uid = useId().replace(/:/g, '');
    const paper = theme.palette.background.paper;
    const tick = { fontSize: 10, fontFamily: 'Inter', fill: theme.palette.text.primary };
    const grid = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={sparkline ? NO_MARGIN : MARGIN}>
                <defs>
                    {series.filter((s) => s.stops).map((s) => (
                        <linearGradient key={s.key} id={`${uid}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                            {s.stops.map(([offset, color, opacity]) => (
                                <stop key={offset} offset={`${offset}%`} stopColor={color} stopOpacity={opacity} />
                            ))}
                        </linearGradient>
                    ))}
                </defs>
                {!sparkline && <CartesianGrid stroke={grid} vertical={false} />}
                <XAxis dataKey={xKey} hide={sparkline} tick={tick} tickLine={false} axisLine={false} minTickGap={16} {...xAxis} />
                <YAxis hide={sparkline} tick={tick} tickLine={false} axisLine={false} width={36} {...yAxis} />
                <Tooltip contentStyle={{ background: paper, fontSize: 11, fontFamily: 'Inter' }} {...tooltip} />
                {series.map((s) => (
                    <Area
                        key={s.key}
                        type="monotone"
                        dataKey={s.key}
                        name={s.name}
                        stroke={s.color}
                        strokeWidth={s.width ?? 2.5}
                        strokeDasharray={s.dashed ? '5 5' : undefined}
                        fill={s.stops ? `url(#${uid}-${s.key})` : 'none'}
                        dot={false}
                        activeDot={{ r: 5, fill: s.color, stroke: paper, strokeWidth: 2 }}
                        isAnimationActive={animate}
                    />
                ))}
            </AreaChart>
        </ResponsiveContainer>
    );
}
